import { decrypt } from '@/common/securityUtils';
import { useAttributes } from '@/hooks/useAttributes';
import { PaginatedApiResponse } from '@/react-query/context/ApiClientContextController';
import { useQuery } from '@/react-query/hooks/useQuery/useQuery';
import { hostListQueries } from '@/services/hostManagement/hostManagement.queries';
import { HostListItem } from '@/services/hostManagement/hostManagement.types';
import { useMemo } from 'react';

type RankOption = { label: string; value: string };

const normalizeRank = (rank: HostListItem['rank']) => {
  const numericRank = Number(rank);

  if (!Number.isInteger(numericRank) || numericRank <= 0) return null;

  return numericRank;
};

export const useAvailableRankOptions = (clubId?: string, currentHostCode?: string) => {
  const { data: rankOptions = [] } = useAttributes('rank') || {};

  const { data: hostListResponse } = useQuery<PaginatedApiResponse<HostListItem[]>>({
    ...hostListQueries.list({ clubId, row: 0, fetch: 100 }),
    enabled: !!clubId,
  });
  const hostList = useMemo(() => hostListResponse?.data ?? [], [hostListResponse]);

  const availableRankOptions = useMemo<RankOption[]>(() => {
    if (!clubId) return rankOptions;

    const occupiedRanks = new Set<number>();

    hostList.forEach((host) => {
      if (currentHostCode && host.hostCode === currentHostCode) return;

      const rankValue = normalizeRank(host.rank);
      if (rankValue) occupiedRanks.add(rankValue);
    });

    return rankOptions.filter((option) => {
      const numericOption = Number(decrypt(option.value));
      if (!Number.isFinite(numericOption)) return true;

      return !occupiedRanks.has(numericOption);
    });
  }, [clubId, currentHostCode, hostList, rankOptions]);

  return availableRankOptions;
};
